import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faGift, faMoneyBill1Wave, faTimes, faCreditCard, faCoins } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../styles/theme';
import provinceSigle from './provinces';
import countries from './countries';
import { toast } from 'react-toastify';
import { getEventDetail, payTickets } from './api';

// Colonne mostrate (le altre chiavi della riga — DealId, GiftCard, MetodoDiPagamento — servono solo alle icone)
const HEADERS = ['Ticket', 'Pagamento', 'Validato'];

// readOnly: nessuna selezione né pagamento, resta la ricerca e la vista stato dei ticket
function Table({ data, event, readOnly = false }) {
    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState(data);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRows, setSelectedRows] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedNation, setSelectedNation] = useState('IT');
    const [fiscalCodeError, setFiscalCodeError] = useState('');
    const [formErrors, setFormErrors] = useState({});
    const [activeTab, setActiveTab] = useState('Carta');

    useEffect(() => {
        setTableData(data || []);
    }, [data]);

    const headers = HEADERS;
    const canSelect = !readOnly;

    const reloadData = async () => {
        try {
            const responseData = await getEventDetail(event);
            setTableData(responseData.tickets || []);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    // Filtra i dati della tabella in base al termine di ricerca
    const filteredData = tableData.filter(row =>
        (row.Ticket || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    const emptyMessage = tableData.length === 0 ? 'Nessun ticket per questo evento' : 'Nessun ticket trovato';

    const handleRowSelection = (row) => {
        setSelectedRows((prevSelectedRows) => {
            if (prevSelectedRows.includes(row)) {
                // Deselect the row
                return prevSelectedRows.filter(selectedRow => selectedRow !== row);
            } else {
                // Select the row
                return [...prevSelectedRows, row];
            }
        });
    };

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setFormErrors({});
        setFiscalCodeError('');
        setIsModalOpen(false);
        setActiveTab('Carta');
        setSelectedNation('IT');
        window.scrollTo(0, 0); // Scroll to the top of the page
    };

    const validateFiscalCode = (code) => {
        // Simple regex for Italian fiscal code validation
        const fiscalCodeRegex = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;
        return fiscalCodeRegex.test(code);
    };

    const handleFiscalCodeChange = (e) => {
        const code = e.target.value;
        if (!validateFiscalCode(code)) {
            setFiscalCodeError('Codice fiscale non valido');
        } else {
            setFiscalCodeError('');
        }
    };

    const validateCap = (cap) => {
        // Updated regex for CAP validation (exactly 5 digits)
        const capRegex = /^[0-9]{5}$/;
        return capRegex.test(cap);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const form = e.target;

        if (activeTab === 'Carta') {
            const errors = {};

            // Check each required field
            ['nome_123', 'cognome_123', 'codiceFiscale_123', 'indirizzo_123', 'city_123', 'nazione_123'].forEach(field => {
                if (!form[field].value) {
                    errors[field] = 'Questo campo è richiesto';
                }
            });

            // Validate CAP
            if (!validateCap(form['cap_123'].value)) {
                errors['cap_123'] = 'CAP non valido';
            }

            setFormErrors(errors);

            if (Object.keys(errors).length > 0) {
                setLoading(false);
                return;
            }
        }

        const clientData = { metodoPagamento: activeTab };

        if (activeTab === 'Carta') {
            clientData.nome = form['nome_123']?.value || '';
            clientData.cognome = form['cognome_123']?.value || '';
            clientData.codiceFiscale = form['codiceFiscale_123']?.value || '';
            clientData.indirizzo = form['indirizzo_123']?.value || '';
            clientData.city = form['city_123']?.value || '';
            clientData.nazione = form['nazione_123']?.value || '';
            clientData.provincia = selectedNation === 'IT' ? provinceSigle[form['provincia_123']?.value] : undefined;
            clientData.cap = selectedNation === 'IT' ? form['cap_123']?.value : '00000';
        }

        const payload = {
            tickets: selectedRows.map(row => row.Ticket),
            deals: selectedRows.map(row => row.DealId),
            client: clientData,
        };

        try {
            await payTickets(payload);
            await reloadData();
            setSelectedRows([]);
            handleCloseModal();
            toast.success('Fattura creata correttamente');
        } catch (error) {
            console.error('Error:', error);
            if (error.status === 400) {
                // Handle fiscal code validation error
                setFiscalCodeError('Codice fiscale non valido');
                toast.error('Codice fiscale non valido');
            } else if (error.status === 500) {
                toast.error('Errore interno, se il problema persiste contattare il referente tecnico');
            } else if (error.status) {
                toast.error('Errore di autenticazione, chiudere e riaprire la pagina/app.');
            } else {
                toast.error('Errore di rete, riprovare.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    return (
        <div style={styles.tableContainer}>
            {canSelect && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {selectedRows.length > 0 ? (
                        <FontAwesomeIcon icon={faMoneyBill1Wave} style={styles.cashIcon} onClick={handleOpenModal} />
                    ) : (
                        <FontAwesomeIcon icon={faMoneyBill1Wave} style={styles.disabledCashIcon} />
                    )}
                </div>
            )}
            <div style={styles.searchContainer}>
                <div style={styles.searchInputContainer}>
                    <input
                        type="text"
                        placeholder="Cerca Ticket"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={styles.searchInput}
                    />
                    {searchTerm && (
                        <FontAwesomeIcon
                            icon={faTimes}
                            style={styles.clearIcon}
                            onClick={() => setSearchTerm('')}
                        />
                    )}
                </div>
                {canSelect && selectedRows.length > 0 &&
                    <span style={styles.searchInput}>Ticket selezionati: <strong>{selectedRows.length}</strong></span>
                }
            </div>
            <table style={styles.table}>
                <thead>
                    <tr>
                        {canSelect && <th style={{ ...styles.th, width: '20px' }}></th>}
                        {headers.map((header, index) => (
                            <th style={styles.th} key={index}>{header}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {filteredData.length === 0 && (
                        <tr>
                            <td style={{ ...styles.td, textAlign: 'center' }} colSpan={headers.length + (canSelect ? 1 : 0)}>
                                {emptyMessage}
                            </td>
                        </tr>
                    )}
                    {filteredData.map((row, index) => (
                        <tr
                            key={row.DealId || index}
                            style={{
                                ...styles.td,
                                backgroundColor: canSelect && selectedRows.includes(row) ? styles.selectedRow.backgroundColor : (index % 2 === 0 ? styles.evenRow.backgroundColor : styles.oddRow.backgroundColor),
                            }}
                        >
                            {canSelect && (
                                <td style={styles.td}>
                                    {!row['GiftCard'] && !row['Pagamento'] && (
                                        <input
                                            type="checkbox"
                                            checked={selectedRows.includes(row)}
                                            onChange={() => handleRowSelection(row)}
                                        />
                                    )}
                                </td>
                            )}
                            {headers.map((header) => (
                                <td
                                    style={{
                                        ...styles.td,
                                        textAlign: header === 'Validato' || header === 'Ticket' || header === 'Pagamento' ? 'center' : 'left',
                                    }}
                                    key={header}
                                >
                                    {header === 'Validato' ? (
                                        row[header] ? <FontAwesomeIcon icon={faCheck} /> : ''
                                    ) : (
                                        header === 'Ticket' ? (
                                            <strong>{row[header]}</strong>
                                        ) : (
                                            header === 'Pagamento' ? (
                                                row['GiftCard'] ? (
                                                    <FontAwesomeIcon icon={faGift} style={{ color: '#b70e0e' }} />
                                                ) : row[header] ? (
                                                    row['MetodoDiPagamento'] === 'Carta' ? (
                                                        <FontAwesomeIcon icon={faCreditCard} />
                                                    ) : row['MetodoDiPagamento'] === 'Contanti' ? (
                                                        <FontAwesomeIcon icon={faCoins} style={{ color: '#db8614' }} />
                                                    ) : (
                                                        <FontAwesomeIcon icon={faCheck} />
                                                    )
                                                ) : ''
                                            ) : (
                                                row[header]
                                            )
                                        )
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>

            {canSelect && isModalOpen && (
                <Modal onClose={handleCloseModal} selectedRows={selectedRows}>
                    <div style={styles.tabContainer}>
                        <div
                            style={{
                                ...styles.tabButton,
                                ...(activeTab === 'Carta' ? styles.activeTab : {}),
                            }}
                            onClick={() => handleTabChange('Carta')}
                        >
                            Carta
                        </div>
                        <div
                            style={{
                                ...styles.tabButton,
                                ...(activeTab === 'Contanti' ? styles.activeTab : {}),
                            }}
                            onClick={() => handleTabChange('Contanti')}
                        >
                            Contanti
                        </div>
                    </div>
                    <form style={styles.form} autoComplete="off" onSubmit={handleSubmit}>
                        {activeTab === 'Carta' && (
                            <>
                                <div style={styles.formRow}>
                                    <label style={styles.formField}>
                                        Nome:
                                        <input type="text" name="nome_123" id="nome_123" required autoComplete="new-password" />
                                        {formErrors.nome_123 && <span style={styles.errorText}>{formErrors.nome_123}</span>}
                                    </label>
                                    <label style={styles.formField}>
                                        Cognome:
                                        <input type="text" name="cognome_123" id="cognome_123" required autoComplete="new-password" />
                                        {formErrors.cognome_123 && <span style={styles.errorText}>{formErrors.cognome_123}</span>}
                                    </label>
                                </div>
                                <div style={styles.formRow}>
                                    <label style={styles.formField}>
                                        Codice Fiscale:
                                        <input
                                            type="text"
                                            name="codiceFiscale_123"
                                            id="codiceFiscale_123"
                                            required
                                            autoComplete="new-password"
                                            onChange={handleFiscalCodeChange}
                                        />
                                        {fiscalCodeError && <span style={styles.errorText}>{fiscalCodeError}</span>}
                                        {formErrors.codiceFiscale_123 && <span style={styles.errorText}>{formErrors.codiceFiscale_123}</span>}
                                    </label>
                                </div>
                                <label>
                                    Indirizzo (senza numero civico):
                                    <input type="text" name="indirizzo_123" id="indirizzo_123" required autoComplete="new-password" />
                                    {formErrors.indirizzo_123 && <span style={styles.errorText}>{formErrors.indirizzo_123}</span>}
                                </label>
                                <div style={styles.formRow}>
                                    <label style={styles.formField}>
                                        Città:
                                        <input type="text" name="city_123" id="city_123" required autoComplete="new-password" />
                                        {formErrors.city_123 && <span style={styles.errorText}>{formErrors.city_123}</span>}
                                    </label>
                                    <label style={styles.formField}>
                                        Nazione:
                                        <select
                                            className="select"
                                            name="nazione_123"
                                            id="nazione_123"
                                            required
                                            value={selectedNation}
                                            onChange={(e) => setSelectedNation(e.target.value)}
                                            style={{
                                                ...styles.input,
                                                width: '100%',
                                                padding: '8px',
                                                borderRadius: '4px',
                                                border: `1px solid ${theme.colors.border}`,
                                            }}
                                        >
                                            {Object.entries(countries).map(([countryName, countryCode]) => (
                                                <option key={countryCode} value={countryCode}>
                                                    {countryName}
                                                </option>
                                            ))}
                                        </select>
                                        {formErrors.nazione_123 && <span style={styles.errorText}>{formErrors.nazione_123}</span>}
                                    </label>
                                </div>
                                {selectedNation === 'IT' && (
                                    <div style={styles.formRow}>
                                        <label style={styles.formField}>
                                            Provincia:
                                            <select
                                                className="select"
                                                name="provincia_123"
                                                id="provincia_123"
                                                required
                                                defaultValue=""
                                                style={{
                                                    ...styles.input,
                                                    width: '100%',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    border: `1px solid ${theme.colors.border}`,
                                                }}
                                            >
                                                <option value="" disabled>Seleziona...</option>
                                                {Object.keys(provinceSigle).map((province) => (
                                                    <option key={province} value={province}>
                                                        {province}
                                                    </option>
                                                ))}
                                            </select>
                                            {formErrors.provincia_123 && <span style={styles.errorText}>{formErrors.provincia_123}</span>}
                                        </label>
                                        <label style={styles.formField}>
                                            CAP:
                                            <input type="text" name="cap_123" id="cap_123" required autoComplete="new-password" />
                                            {formErrors.cap_123 && <span style={styles.errorText}>{formErrors.cap_123}</span>}
                                        </label>
                                    </div>
                                )}
                            </>
                        )}
                        {loading ? (
                            <div style={styles.spinnerContainer}>
                                <div style={styles.spinner}></div>
                            </div>
                        ) : (
                            <button type="submit" style={styles.submitButton} disabled={loading}>Crea Fattura</button>
                        )}
                    </form>
                </Modal>
            )}
        </div>
    );
}

// Define a simple Modal component
function Modal({ children, onClose, selectedRows }) {
    const totalAmount = selectedRows.length * 15;  // Calculate the total amount

    return (
        <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
                <button onClick={onClose} style={styles.closeButton}>
                    <FontAwesomeIcon icon={faTimes} style={styles.closeIcon} />
                </button>
                <h2 style={styles.modalTitle}>Dati Fatturazione</h2>
                <p style={styles.modalDescription}>Ticket selezionati: <strong>{selectedRows.length}</strong></p>
                <p style={styles.modalDescription}>Importo totale: <strong>{totalAmount} €</strong></p>
                {children}
            </div>
        </div>
    );
}

const styles = {
    tableContainer: {
        width: '100%',
        overflowX: 'auto',
        maxWidth: '800px',  // Limita la larghezza massima
        margin: '0 auto',   // Centra la tabella
        marginTop: '25px',
        marginBottom: '50px',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px',
        textAlign: 'left',
    },
    th: {
        padding: '12px 15px',
        backgroundColor: '#E97BA6',
        color: theme.colors.white,
        fontWeight: 'bold',
        borderBottom: `1px solid ${theme.colors.border}`,
        textAlign: 'center',  // Center the text in the headers
    },
    td: {
        padding: '10px 15px',
        borderBottom: `1px solid ${theme.colors.border}`,
    },
    evenRow: {
        backgroundColor: theme.colors.white,
    },
    oddRow: {
        backgroundColor: theme.colors.background,
    },
    input: {
        padding: 0,  // Remove padding
        fontSize: '16px',  // Imposta la dimensione del font a 16px
    },
    spinnerContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%', // Ensures the spinner takes the full height of the cell
    },
    spinner: {
        border: '2px solid rgba(0, 0, 0, 0.1)',
        borderRadius: '50%',
        borderTop: '2px solid #3498db',
        width: '30px',  // Increased width
        height: '30px', // Increased height
        animation: 'spin 1s linear infinite',
    },
    searchInput: {
        marginBottom: '10px',
        padding: '8px',
        width: '125px',
        borderRadius: '4px',
        border: `1px solid ${theme.colors.border}`,
        fontSize: '16px',  // Imposta la dimensione del font a 16px
    },
    searchContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
        width: '100%',
    },
    searchInputContainer: {
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
    },
    selectedRow: {
        backgroundColor: '#e97ba655', // Light gray color for selected rows
    },
    collectDataButton: {
        marginLeft: 'auto',
        padding: '8px 16px',
        backgroundColor: '#28a745', // Green color for the button
        color: theme.colors.white,
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        display: 'flex', // To align icon and text
        alignItems: 'center', // Center icon and text vertically
    },
    cashIcon: {
        marginRight: '8px',
        color: '#4ba74e',
        fontSize: '30px',
        cursor: 'pointer',
    },
    disabledCashIcon: {
        marginRight: '8px',
        color: '#cccccc', // Gray color for disabled state
        fontSize: '30px',
        cursor: 'default', // No pointer cursor
    },
    notesIcon: {
        color: '#db8614',
        fontSize: '30px',
        cursor: 'pointer',
    },
    clearIcon: {
        marginLeft: '8px',
        color: 'red',
        cursor: 'pointer',
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalContent: {
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: '8px',
        width: '400px',
        maxWidth: '90%',
        position: 'relative',
        paddingTop: '40px',
    },
    closeButton: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
    },
    closeIcon: {
        color: 'red',
        fontSize: '16px',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
    },
    formRow: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    formField: {
        flex: '1',
        marginRight: '10px',
        fontSize: '16px',  // Imposta la dimensione del font a 16px
    },
    submitButton: {
        marginTop: '0px', // Add space below the button
    },
    modalTitle: {
        marginTop: '0px',
        marginBottom: '20px',
        fontSize: '18px',
        fontWeight: 'bold',
    },
    modalDescription: {
        marginBottom: '20px',
        fontSize: '14px',
        color: theme.colors.text,
    },
    errorText: {
        color: 'red',
        fontSize: '12px',
        marginTop: '5px',
    },
    tabContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '20px',
    },
    tabButton: {
        padding: '10px 20px',
        cursor: 'pointer',
        border: 'none',
        borderRadius: '4px 4px 0 0',
        width: '50%',  // Set width to 50%
        textAlign: 'center',  // Center the text
        fontWeight: 'bold',
        backgroundColor: '#f1f1f1',  // Default background color for both tabs
        color: '#000',  // Default text color for both tabs
        display: 'inline-block',  // Ensure div behaves like a button
        textTransform: 'uppercase',
        fontSize: '10px',
    },
    activeTab: {
        color: '#000',  // Text color for the active tab
        borderBottom: '5px solid #ee8bb9',  // Add bottom border for active tab
    },
};

export default Table;
